import { useState, useEffect, useCallback } from 'react';
import {
  Send, Loader2, RefreshCw, Wallet, ArrowRight, Eye, EyeOff, Code, AlertTriangle,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


function ResultPanel({ result, error, onClear }) {
  const [showRaw, setShowRaw] = useState(false);
  if (!result && !error) return null;
  const ok = !!(result && result.ok);
  return (
    <div className="mt-5 border rounded-lg overflow-hidden" data-testid="admin-transfer-result">
      <div className={`px-4 py-3 flex items-center justify-between ${ok ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}`}>
        <div className="flex items-center gap-2">
          {ok ? '✅' : '❌'}
          <div>
            <div className={`text-sm font-semibold ${ok ? 'text-green-800' : 'text-red-800'}`}>
              {ok ? 'Transferencia ejecutada' : 'Error'}
            </div>
            {ok && (
              <div className="text-xs text-slate-600">
                VES {result.amount?.toFixed?.(2) || result.amount} · ref banco: <span className="font-mono">{result.bank_reference || 'pendiente'}</span>
              </div>
            )}
            {error && <div className="text-xs text-red-700">{error}</div>}
          </div>
        </div>
        {result && (
          <button onClick={() => setShowRaw(s => !s)} className="text-xs px-2 py-1 bg-white border rounded inline-flex items-center gap-1">
            {showRaw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showRaw ? 'Compacta' : 'JSON'}
          </button>
        )}
        <button onClick={onClear} className="text-xs px-2 py-1 text-slate-500 hover:text-slate-800">Cerrar</button>
      </div>
      {result && showRaw && (
        <pre className="bg-slate-900 text-green-300 p-3 overflow-x-auto text-xs" data-testid="admin-transfer-raw">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}


export default function AdminTransferTab({ setToast }) {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('pooling');
  const [savedList, setSavedList] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [form, setForm] = useState({
    dest_account: '',
    dest_rif: '',
    dest_bank_code: '',
    dest_name: '',
    dest_doc_type: 'V',
    amount: '',
    concepto: '',
  });
  const [saveBenef, setSaveBenef] = useState(false);
  const [benefAlias, setBenefAlias] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  const loadSources = useCallback(async () => {
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/super-admin/admin-transfer/source-accounts`, { headers: { Authorization: `Bearer ${tk}` } });
      setSources(r.data.sources || []);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error al cargar cuentas origen' });
    }
  }, [setToast]);

  const loadSaved = useCallback(async () => {
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/super-admin/saved-beneficiaries`, { headers: { Authorization: `Bearer ${tk}` } });
      setSavedList(r.data || []);
    } catch { /* silent */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/super-admin/admin-transfer/history?limit=30`, { headers: { Authorization: `Bearer ${tk}` } });
      setHistory(r.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSources(); loadSaved(); loadHistory(); }, [loadSources, loadSaved, loadHistory]);

  const filteredSaved = (() => {
    const term = search.trim().toLowerCase();
    if (!term) return savedList;
    return savedList.filter(b =>
      (b.alias || '').toLowerCase().includes(term)
      || (b.account_number || '').includes(term)
      || (b.holder_id || '').toLowerCase().includes(term)
      || (b.holder_name || '').toLowerCase().includes(term)
    );
  })();

  const applyBeneficiary = (b) => {
    setForm(f => ({
      ...f,
      dest_account: b.account_number,
      dest_rif: b.holder_id,
      dest_bank_code: b.bank_code,
      dest_name: b.holder_name,
      dest_doc_type: b.holder_doc_type || 'V',
    }));
    setSearch(b.alias);
    setShowDropdown(false);
    setSaveBenef(false);
    setBenefAlias('');
  };

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const selectedSourceObj = sources.find(s => s.key === selectedSource);
  const isInterbank = selectedSourceObj && form.dest_bank_code && form.dest_bank_code !== selectedSourceObj.bank_code;

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSourceObj) {
      setToast?.({ type: 'error', message: 'Selecciona una cuenta origen' });
      return;
    }
    const isMerchantSource = !!selectedSourceObj.is_merchant_account;
    const merchantWarning = isMerchantSource
      ? `\n\n⚠️ ATENCIÓN: La cuenta origen es del MERCHANT (${selectedSourceObj.account_holder_name}), NO de Caura Pay. Caura ejecuta el CCE en su nombre por autorización de tercero ante Banco Plaza. Este movimiento NO toca wallets internos.`
      : '';
    const summary = `Confirmar transferencia\n\nDESDE: ${selectedSourceObj.label} (...${selectedSourceObj.account_number.slice(-4)})\nHACIA: ${form.dest_name} (...${form.dest_account.slice(-4)})\nMONTO: VES ${form.amount}\n${isInterbank ? '\n⚠️ Interbancario · Banco Plaza cobrará comisión 0.30% adicional' : ''}${merchantWarning}`;
    if (!window.confirm(summary)) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const tk = localStorage.getItem('token');
      const payload = {
        source_key: selectedSource,
        ...form,
        amount: parseFloat(form.amount),
        save_beneficiary: saveBenef,
        beneficiary_alias: benefAlias.trim() || undefined,
      };
      const r = await axios.post(`${BACKEND_URL}/api/super-admin/admin-transfer/execute`, payload, { headers: { Authorization: `Bearer ${tk}` } });
      setResult(r.data);
      setToast?.({ type: 'success', message: `Ejecutado · VES ${r.data.amount} · ref ${r.data.bank_reference || 'pendiente'}` });
      loadHistory();
      loadSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setError(msg);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-transfer-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Transferencias Administrativas</h2>
          <p className="text-sm text-slate-600 mt-1">
            CCE manual desde cuentas Caura Pay hacia cualquier destino. <strong>No toca wallets de merchants</strong>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form */}
        <div className="lg:col-span-2 bg-white border rounded-lg p-5">
          <form onSubmit={submit} className="space-y-5">
            {/* Origen */}
            <div>
              <h4 className="text-xs font-bold uppercase text-slate-500 mb-2 tracking-wide flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Cuenta origen (Caura Pay)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {sources.map(s => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSelectedSource(s.key)}
                    data-testid={`source-${s.key}`}
                    className={`text-left p-3 border-2 rounded-lg transition ${
                      selectedSource === s.key
                        ? (s.is_merchant_account ? 'border-orange-600 bg-orange-50' : 'border-blue-600 bg-blue-50')
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold" style={{ color: '#051C2C' }}>{s.label}</div>
                      {s.is_merchant_account && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-orange-200 text-orange-900 rounded">
                          Merchant
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">{s.account_number}</div>
                    <div className="text-[11px] text-slate-500">Banco {s.bank_code} · RIF {s.account_holder_id}</div>
                    <div className="text-[10px] text-slate-400 mt-1 italic">{s.purpose}</div>
                  </button>
                ))}
              </div>
            </div>

            <ArrowRight className="w-5 h-5 mx-auto text-slate-300" />

            {/* Destino */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wide">Destino</h4>
              </div>

              <div className="relative mb-3">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                  placeholder="🔍 Buscar cuenta guardada por alias, cuenta, RIF o titular…"
                  data-testid="admin-saved-search"
                  className="w-full px-3 py-2 border rounded text-sm bg-slate-50 focus:bg-white"
                />
                {showDropdown && filteredSaved.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {filteredSaved.slice(0, 30).map(b => (
                      <button
                        key={b.beneficiary_id}
                        type="button"
                        // onMouseDown con preventDefault evita que el input pierda foco antes del click.
                        onMouseDown={(e) => { e.preventDefault(); applyBeneficiary(b); }}
                        data-testid={`admin-saved-${b.beneficiary_id}`}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                      >
                        <div className="text-sm font-medium" style={{ color: '#051C2C' }}>{b.alias}</div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          {b.account_number} · {b.bank_code} · {b.holder_doc_type}{b.holder_id} · {b.holder_name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500">Cuenta (20 dígitos)</label>
                  <input value={form.dest_account} onChange={update('dest_account')} data-testid="admin-dest-account"
                    className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} required />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">RIF/Cédula titular (con letra V/E/J/G)</label>
                  <input value={form.dest_rif} onChange={update('dest_rif')} data-testid="admin-dest-rif"
                    className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="V12345678" required />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Banco destino (4 dígitos)</label>
                  <input value={form.dest_bank_code} onChange={update('dest_bank_code')} data-testid="admin-dest-bank"
                    className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={4} required />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Tipo doc.</label>
                  <select value={form.dest_doc_type} onChange={update('dest_doc_type')} data-testid="admin-dest-doctype"
                    className="w-full px-3 py-1.5 border rounded text-sm">
                    <option value="V">V (Venezolano)</option>
                    <option value="E">E (Extranjero)</option>
                    <option value="J">J (Jurídico)</option>
                    <option value="G">G (Gobierno)</option>
                    <option value="P">P (Pasaporte)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-slate-500">Titular destino</label>
                  <input value={form.dest_name} onChange={update('dest_name')} data-testid="admin-dest-name"
                    className="w-full px-3 py-1.5 border rounded text-sm" required />
                </div>
              </div>

              {isInterbank && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Interbancario:</strong> Banco Plaza cobrará comisión 0.30% adicional sobre el monto ejecutado. Esta comisión sale de la cuenta origen seleccionada.
                  </div>
                </div>
              )}

              <div className="mt-3 rounded-lg border bg-slate-50 p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={saveBenef} onChange={(e) => setSaveBenef(e.target.checked)} data-testid="admin-save-benef" className="rounded" />
                  <span className="text-sm font-medium text-slate-700">Guardar cuenta destino para futuros usos</span>
                </label>
                {saveBenef && (
                  <div className="mt-2">
                    <input value={benefAlias} onChange={(e) => setBenefAlias(e.target.value)}
                      placeholder={`Alias, p.ej. "${form.dest_name}"`}
                      data-testid="admin-benef-alias"
                      maxLength={80}
                      className="w-full px-3 py-1.5 border rounded text-sm" />
                  </div>
                )}
              </div>
            </div>

            {/* Monto y concepto */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500">Monto VES</label>
                <input type="number" step="0.01" min="0.01" value={form.amount} onChange={update('amount')}
                  data-testid="admin-amount"
                  className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold" required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Concepto</label>
                <input value={form.concepto} onChange={update('concepto')} data-testid="admin-concepto"
                  className="w-full px-3 py-1.5 border rounded text-sm" maxLength={80}
                  placeholder="Ej: Pago a proveedor X" />
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t">
              <button type="submit" disabled={busy} data-testid="admin-transfer-submit"
                className="px-5 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? 'Ejecutando…' : 'Ejecutar transferencia'}
              </button>
            </div>
          </form>

          <ResultPanel result={result} error={error} onClear={() => { setResult(null); setError(null); }} />
        </div>

        {/* History sidebar */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold" style={{ color: '#051C2C' }}>Historial reciente</h4>
            <button onClick={loadHistory} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="admin-history-refresh">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-6">Aún no hay transferencias admin.</div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {history.map(h => {
                const ok = h.status === 'success';
                return (
                  <div
                    key={h.transfer_id}
                    className={`border-l-4 rounded p-2 text-xs hover:bg-slate-50 ${ok ? 'border-l-green-500 bg-green-50/40' : 'border-l-red-500 bg-red-50/40'}`}
                    data-testid={`admin-history-row-${h.transfer_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-1">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ok ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'}`}>
                          {ok ? '✓ OK' : `✗ ${h.bank_response_code || 'FAIL'}`}
                        </span>
                        <span className="font-semibold text-slate-700">
                          VES {(Number(h.amount) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-400">
                        {h.created_at ? new Date(h.created_at).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      <span className="font-mono">...{(h.source_account || '????').slice(-4)}</span>
                      <ArrowRight className="inline w-3 h-3 mx-1" />
                      <span className="font-mono">...{(h.dest_account || '????').slice(-4)}</span>
                      <span className="ml-1 text-slate-400">({h.dest_bank_code})</span>
                    </div>
                    <div className="text-[11px] text-slate-700 mt-0.5 truncate" title={h.dest_name}>
                      → <span className="font-medium">{h.dest_name}</span>
                      {h.dest_rif && <span className="text-slate-400 ml-1 font-mono">({h.dest_rif})</span>}
                    </div>
                    {h.concepto && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate italic" title={h.concepto}>
                        “{h.concepto}”
                      </div>
                    )}
                    {ok && h.bank_reference && (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">ref banco: {h.bank_reference}</div>
                    )}
                    {!ok && h.error_msg && (
                      <div className="text-[10px] text-red-700 mt-0.5 line-clamp-2" title={h.error_msg}>
                        ⚠ {h.error_msg}
                      </div>
                    )}
                    <div className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider">
                      {({
                        admin_transfer: 'Admin',
                        expired_refund: 'Refund Expirado',
                        orphan_refund: 'Refund Huérfano',
                      })[h.transfer_type] || 'Manual'} · {h.executed_by_email || ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
